import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, canonicalStoredFileIdForApiPath, getApiErrorMessage } from '../lib/api'
import { resolveAsyncBlobResponse } from '../lib/asyncOperation'
import { useToast } from '../ui/ToastProvider'
import {
  accidentTypeFromApplication,
  resolveListedClaimFaxNumber,
} from '../lib/claimFaxRouting'
import { claimDocumentPageCount, claimDocumentsPageCount } from '../lib/claimDocumentPages'
import { CLAIM_DOCUMENT_TYPE_OPTIONS } from '../lib/claimDocumentTypes'
import { formatApiDateTimeDisplay, formatMenuInstant } from '../lib/date'

function claimAttachmentPreviewKind(doc) {
  const name = String(doc?.label || doc?.fileId || '').toLowerCase()
  if (/\.(png|jpe?g|webp|gif|bmp|svg)$/.test(name)) return 'image'
  if (/\.pdf$/.test(name)) return 'pdf'
  return 'file'
}

async function readBlobApiErrorMessage(error, fallback) {
  const blob = error?.response?.data
  if (blob && typeof blob.text === 'function') {
    try {
      const text = (await blob.text()).trim()
      if (text.startsWith('{')) {
        const parsed = JSON.parse(text)
        if (parsed?.message) return parsed.message
      }
      if (text) return text
    } catch {
      // ignore
    }
  }
  return getApiErrorMessage(error, fallback)
}

const FAX_PROGRESS_STEPS = [
  { key: 'received', label: '청구접수' },
  { key: 'queued', label: '전송대기' },
  { key: 'sending', label: '전송중' },
  { key: 'done', label: '전송완료' },
]

function getFaxProgressUi(app) {
  if (!app) {
    return {
      index: 0,
      tone: 'idle',
      title: '청구 정보 확인 중',
      description: '',
    }
  }
  const r = app.faxReservationStatus
  const q = app.faxQuotaSettlementStatus
  const failureReason = app.faxLastError || app.faxQuotaLastError || ''

  if (q === 'CONSUMED') {
    return {
      index: 3,
      tone: 'success',
      title: '전송완료',
      description: '보험사 팩스 수신이 확인되었습니다.',
    }
  }
  if (r === 'FAILED' || q === 'SETTLEMENT_FAILED' || (q === 'RELEASED' && r !== 'CANCELLED')) {
    return {
      index: 2,
      tone: 'danger',
      title: '전송실패',
      description: failureReason || '수신번호와 청구서류를 확인한 뒤 재시도해 주세요.',
    }
  }
  if (r === 'CANCELLED') {
    return {
      index: 1,
      tone: 'muted',
      title: '전송취소',
      description: '보험사로 전송되기 전에 취소되었습니다.',
    }
  }
  if (r === 'SENDING' || r === 'SENT' || q === 'PENDING' || app.faxTransmissionId) {
    return {
      index: 2,
      tone: 'progress',
      title: '전송중',
      description: 'Popbill 접수 후 보험사 수신 결과를 기다리고 있습니다.',
    }
  }
  if (r === 'SCHEDULED') {
    return {
      index: 1,
      tone: 'waiting',
      title: '전송대기',
      description: '예약된 시각에 보험사 팩스 전송이 시작됩니다.',
    }
  }
  if (app.status === 'DRAFT') {
    return {
      index: 0,
      tone: 'idle',
      title: '작성중',
      description: '청구 정보, 첨부 서류, 서명을 완료한 뒤 보험사 발송을 진행합니다.',
    }
  }
  return {
    index: 0,
    tone: 'ready',
    title: '청구접수',
    description: '청구 정보가 저장되었습니다. 보험사 팩스 발송을 진행할 수 있습니다.',
  }
}

function DetailInfoItem({ label, value, children, strong = false, multiline = false, className = '' }) {
  const content = children ?? value ?? '-'
  const title = typeof content === 'string' && !multiline ? content : undefined

  return (
    <div className={`rounded border border-slate-200 bg-white px-3 py-2 ${className}`}>
      <div className="text-[11px] font-semibold text-slate-500">{label}</div>
      <div
        className={`mt-1 min-w-0 text-sm ${
          strong ? 'font-semibold text-slate-950' : 'font-medium text-slate-800'
        } ${multiline ? 'whitespace-pre-wrap break-words leading-5' : 'truncate'}`}
        title={title}
      >
        {content}
      </div>
    </div>
  )
}

function CompactInfoRow({ label, value, children, strong = false }) {
  const content = children ?? value ?? '-'
  const title = typeof content === 'string' ? content : undefined
  return (
    <div className="grid grid-cols-[92px_1fr] items-start gap-3 border-b border-slate-100 py-2 last:border-b-0">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div
        className={`min-w-0 break-words text-sm ${strong ? 'font-semibold text-slate-950' : 'font-medium text-slate-700'}`}
        title={title}
      >
        {content}
      </div>
    </div>
  )
}

function FaxProgressStep({ step, done, active, danger, muted, time }) {
  const markerClass = danger
    ? 'border-rose-500 bg-rose-600 text-white'
    : muted
      ? 'border-slate-300 bg-slate-100 text-slate-500'
      : done || active
        ? 'border-brand-navy bg-brand-navy text-white'
        : 'border-slate-300 bg-white text-slate-400'
  const labelClass = danger
    ? 'text-rose-700'
    : active
      ? 'text-brand-navy'
      : done
        ? 'text-slate-950'
        : 'text-slate-400'

  return (
    <div className="relative flex min-w-0 flex-1 flex-col items-center text-center">
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-bold shadow-sm ${markerClass}`}
        aria-hidden="true"
      >
        {danger ? '!' : done || active ? '✓' : ''}
      </div>
      <div className={`mt-2 text-sm font-semibold ${labelClass}`}>{step.label}</div>
      <div className="mt-1 min-h-[18px] max-w-[150px] truncate text-xs text-slate-500" title={time || undefined}>
        {time || ''}
      </div>
    </div>
  )
}

export default function ClaimApplicationDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { show } = useToast()
  const [application, setApplication] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [downloadingAll, setDownloadingAll] = useState(false)
  const [sendingFax, setSendingFax] = useState(false)
  const [faxModalOpen, setFaxModalOpen] = useState(false)
  const [faxInsurerSetting, setFaxInsurerSetting] = useState(null)
  const [faxForm, setFaxForm] = useState({
    receiveNum: '',
    receiveName: '',
    title: '',
  })
  const [reclaiming, setReclaiming] = useState(false)
  const [documentReviewSavingId, setDocumentReviewSavingId] = useState(null)
  const [previewDocumentIndex, setPreviewDocumentIndex] = useState(null)
  const [documentPreviewUrls, setDocumentPreviewUrls] = useState({})

  const faxListedForVirtualHint = useMemo(
    () => resolveListedClaimFaxNumber(faxInsurerSetting, accidentTypeFromApplication(application)),
    [faxInsurerSetting, application],
  )
  const claimDocuments = useMemo(
    () => (Array.isArray(application?.documents) ? application.documents : []),
    [application],
  )
  const claimDocumentPageCountTotal = useMemo(
    () => claimDocumentsPageCount(claimDocuments),
    [claimDocuments],
  )
  const previewDocument = previewDocumentIndex != null ? claimDocuments[previewDocumentIndex] : null
  const previewDocumentPreview = previewDocument?.fileId ? documentPreviewUrls[String(previewDocument.fileId)] : null
  const previewDocumentKind = previewDocumentPreview?.kind || claimAttachmentPreviewKind(previewDocument)

  useEffect(() => {
    let cancelled = false
    const objectUrls = []
    ;(async () => {
      const next = {}
      for (const doc of claimDocuments) {
        const fileId = doc?.fileId
        if (!fileId) continue
        try {
          const res = await api.get(`/files/${encodeURIComponent(canonicalStoredFileIdForApiPath(fileId))}/preview`, { responseType: 'blob', __silent: true })
          if (cancelled) return
          const contentType = String(res.headers?.['content-type'] || '').toLowerCase()
          const blob = new Blob([res.data], { type: contentType || 'application/octet-stream' })
          const url = URL.createObjectURL(blob)
          objectUrls.push(url)
          next[String(fileId)] = {
            url,
            kind: contentType.includes('image/')
              ? 'image'
              : contentType.includes('pdf')
                ? 'pdf'
                : claimAttachmentPreviewKind(doc),
          }
        } catch {
          next[String(fileId)] = { url: '', kind: claimAttachmentPreviewKind(doc) }
        }
      }
      if (!cancelled) setDocumentPreviewUrls(next)
    })()
    return () => {
      cancelled = true
      objectUrls.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch { }
      })
    }
  }, [claimDocuments])

  const accidentReceiptTypeLabel = useMemo(() => {
    if (!application?.extraFieldsJson) return null
    try {
      const ex = JSON.parse(application.extraFieldsJson)
      const v = ex?.accidentReceiptType
      if (v === 'ADDITIONAL') return '동일사고 추가접수'
      if (v === 'NEW') return '최초청구'
      return null
    } catch {
      return null
    }
  }, [application?.extraFieldsJson])

  useEffect(() => {
    if (id) {
      loadApplication()
    }
  }, [id])

  useEffect(() => {
    if (!faxModalOpen || !application) {
      setFaxInsurerSetting(null)
      return
    }
    const oid = application.insurerOrg?.id ?? application.insurerOrgId
    if (oid == null) {
      setFaxInsurerSetting(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get(`/claims/insurer-fax-settings?insurerOrgIds=${encodeURIComponent(oid)}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const list = Array.isArray(payload) ? payload : []
        const row = list.find((r) => String(r?.insurerOrgId) === String(oid)) || list[0] || null
        if (!cancelled) {
          setFaxInsurerSetting(row
            ? {
              claimFaxNumber: row.claimFaxNumber || '',
              claimFaxRoutingJson: row.claimFaxRoutingJson ?? null,
              virtualFaxRequired: !!row.virtualFaxRequired,
            }
            : null)
        }
      } catch {
        if (!cancelled) setFaxInsurerSetting(null)
      }
    })()
    return () => { cancelled = true }
  }, [faxModalOpen, application])

  useEffect(() => {
    if (!faxModalOpen || !application) return
    const virt = !!faxInsurerSetting?.virtualFaxRequired
    const at = accidentTypeFromApplication(application)
    const def = resolveListedClaimFaxNumber(faxInsurerSetting, at) || faxInsurerSetting?.claimFaxNumber || ''
    setFaxForm({
      receiveNum: virt ? (application.faxReceiveNum || '') : (def || application.faxReceiveNum || ''),
      receiveName: application.faxReceiveName || application.insurerOrg?.name || '',
      title: application.faxTitle || '보험금청구서',
    })
  }, [faxModalOpen, application, faxInsurerSetting])

  const loadApplication = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get(`/claims/applications/${id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setApplication(payload)
    } catch (e) {
      setError('청구 신청서를 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const downloadFile = async (fileId) => {
    try {
      const res = await api.get(`/files/${fileId}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = fileId
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // ignore
    }
  }

  const openDocumentPreview = (doc) => {
    const idx = claimDocuments.findIndex((item) => Number(item.id) === Number(doc?.id))
    setPreviewDocumentIndex(idx >= 0 ? idx : 0)
  }

  const moveDocumentPreview = (direction) => {
    if (!claimDocuments.length) return
    setPreviewDocumentIndex((prev) => {
      const current = Number.isInteger(prev) ? prev : 0
      return (current + direction + claimDocuments.length) % claimDocuments.length
    })
  }

  const updateClaimDocumentType = async (doc, documentType) => {
    if (!doc?.id) return
    setDocumentReviewSavingId(doc.id)
    try {
      await api.put(`/claims/applications/${id}/documents/${doc.id}`, {
        documentType: documentType || null,
        label: doc.label || null,
        sortOrder: doc.sortOrder ?? 0,
      }, { __silent: true })
      show('서류 유형을 저장했습니다.', 'success')
      await loadApplication()
    } catch {
      show('서류 유형 저장에 실패했습니다.', 'error')
    } finally {
      setDocumentReviewSavingId(null)
    }
  }

  const handleDownloadAll = async () => {
    if (!application) return
    setDownloadingAll(true)
    try {
      let res = await api.get(`/claims/applications/${id}/download`, {
        responseType: 'blob',
        __silent: true,
        __suppressGlobal409Toast: true,
      })
      res = await resolveAsyncBlobResponse(res)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const cd = res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'] || ''
      const filenameMatch = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="([^"]+)"/)
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : `보험금청구_${id}.pdf`
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('청구서 PDF를 다운로드했습니다. 내려받은 파일은 고객 청구 업무 목적으로만 사용해 주세요.', 'success')
    } catch (e) {
      const message = await readBlobApiErrorMessage(e, '다운로드에 실패했습니다. 권한과 파일 상태를 확인해 주세요.')
      show(message, 'error')
    } finally {
      setDownloadingAll(false)
    }
  }

  const digitsOnly = (v) => String(v || '').replace(/\D/g, '')

  const handleSendFax = async () => {
    if (!application) return
    if (application.faxQuotaSettlementStatus === 'CONSUMED') {
      show('팩스 전송이 완료된 청구서는 다시 발송할 수 없습니다. 재청구가 필요하면 새 임시저장 청구를 만들어 주세요.', 'error')
      return
    }
    if (!faxForm.receiveNum || !faxForm.receiveName) {
      show('팩스 수신번호와 수신자명을 입력해 주세요. 발송 전 보험사 수신 정보를 다시 확인해 주세요.', 'error')
      return
    }
    if (faxInsurerSetting?.virtualFaxRequired) {
      const a = digitsOnly(faxForm.receiveNum)
      const at = accidentTypeFromApplication(application)
      const b = digitsOnly(resolveListedClaimFaxNumber(faxInsurerSetting, at))
      if (b && a === b) {
        show('이 보험사는 가상팩스번호가 필요합니다. 발급받은 번호를 입력해 주세요.', 'error')
        return
      }
    }
    setSendingFax(true)
    try {
      await api.post(`/claims/applications/${id}/schedule-fax`, {
        receiveNum: faxForm.receiveNum,
        receiveName: faxForm.receiveName,
        title: faxForm.title || '보험금청구서',
        delayMinutes: 1,
      })
      show('청구서류 발송이 예약되었습니다. 약 1분 후 보험사 팩스로 전송되며, 전송 시작 전까지 취소할 수 있습니다.', 'success')
      setFaxModalOpen(false)
      setFaxForm({ receiveNum: '', receiveName: '', title: '' })
      await loadApplication()
    } catch (e) {
      if (e?.response?.status === 404) {
        show('템플릿을 찾을 수 없습니다. 운영자에게 문의하세요.', 'error')
      } else if (String(e?.response?.data?.message || '').includes('이전 팩스 발송 결과가 아직 확정되지 않았습니다')) {
        show('이전 팩스 발송 결과가 아직 확정되지 않았습니다. 최종 결과를 확인한 뒤 다시 시도해 주세요.', 'error')
      } else if (String(e?.response?.data?.message || '').includes('이미 발송 대기 중인 팩스가 있습니다')
        || String(e?.response?.data?.message || '').includes('이미 예약된 팩스가 있습니다')) {
        show('이미 발송 대기 중인 건이 있습니다. 기존 발송을 취소한 뒤 다시 시도해 주세요.', 'error')
      } else {
        const msg = e?.response?.data?.message || e?.response?.data?.error?.message || ''
        show(msg || '팩스 발송에 실패했습니다. 수신번호와 청구서류를 확인한 뒤 다시 시도해 주세요.', 'error')
      }
    } finally {
      setSendingFax(false)
    }
  }

  const handleCancelScheduledFax = async () => {
    if (!application) return
    try {
      await api.post(`/claims/applications/${id}/cancel-scheduled-fax`)
      show('발송 대기를 취소했습니다. 보험사로 전송되기 전 상태로 돌아갔습니다.', 'success')
      await loadApplication()
    } catch {
      show('발송 취소에 실패했습니다. 이미 전송이 시작되었는지 상태를 확인해 주세요.', 'error')
    }
  }

  const handleRetryFaxSettlement = async () => {
    if (!application) return
    try {
      await api.post(`/claims/applications/${id}/retry-fax-settlement`)
      show('팩스 전송 상태를 동기화했습니다.', 'success')
      await loadApplication()
    } catch (e) {
      show(e?.response?.data?.message || '팩스 결과 동기화에 실패했습니다.', 'error')
    }
  }

  const statusLabels = {
    DRAFT: '임시저장',
    SUBMITTED: '접수됨',
    UNDER_REVIEW: '심사중',
    APPROVED: '승인됨',
    REJECTED: '반려됨',
    COMPLETED: '완료됨',
  }

  const faxHistoryKindLabels = {
    RESEND: '발송 재시도',
    SCHEDULE_WAIT: '발송 대기 시작',
    CANCEL_WAIT: '발송 취소',
    FAX_SUBMITTED: '팩스 전송 접수',
    FAX_FAILED: '팩스 발송 실패',
  }

  const handleReclaim = async () => {
    if (!application?.id) return
    setReclaiming(true)
    try {
      const res = await api.post(`/claims/applications/${application.id}/reclaim`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const newId = payload?.id
      if (!newId) {
        show('재청구 건을 만들지 못했습니다.', 'error')
        return
      }
      show('새 임시저장 청구가 만들어졌습니다. 내용을 확인한 뒤 다시 제출해 주세요.', 'success')
      navigate(`/support/claims?resumeApplicationId=${newId}`)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || ''
      show(msg || '재청구에 실패했습니다.', 'error')
    } finally {
      setReclaiming(false)
    }
  }

  const formatHistoryInstant = (iso) => formatApiDateTimeDisplay(undefined, iso)

  if (loading) {
    return <div className="text-center py-8 text-slate-600">불러오는 중...</div>
  }

  if (error || !application) {
    return (
      <div className="space-y-4">
        <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error || '청구 신청서를 찾을 수 없습니다.'}
        </div>
        <button
          type="button"
          className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
          onClick={() => navigate('/support/claims/applications')}
        >
          목록으로
        </button>
      </div>
    )
  }

  const faxProgress = getFaxProgressUi(application)
  const faxTransmissionConsumed = application.faxQuotaSettlementStatus === 'CONSUMED'
  const showFaxRetryAction = application.faxReservationStatus === 'FAILED' || application.faxReservationStatus === 'CANCELLED'
  const faxResultPending = application.faxQuotaSettlementStatus === 'PENDING' && application.faxTransmissionId
  const showFaxSyncAction = application.faxQuotaSettlementStatus === 'SETTLEMENT_FAILED'
    || faxResultPending
  const faxSendDisabled = sendingFax
    || application.faxReservationStatus === 'SCHEDULED'
    || application.faxReservationStatus === 'SENDING'
    || faxResultPending
    || faxTransmissionConsumed
  const faxSendTitle = faxTransmissionConsumed
    ? '팩스 전송이 완료되어 다시 발송할 수 없습니다.'
    : faxResultPending
      ? '이전 팩스 전송 결과를 먼저 확인해 주세요.'
      : application.faxReservationStatus === 'SCHEDULED'
        ? '이미 발송 대기 중입니다. 먼저 발송 취소를 하세요.'
        : application.faxReservationStatus === 'SENDING'
          ? '팩스 발송 처리 중입니다.'
          : undefined
  const faxStepTimes = {
    received: application.submittedAt
      ? formatHistoryInstant(application.submittedAt)
      : formatHistoryInstant(application.createdAt),
    queued: application.faxReservedAt ? formatMenuInstant(application, 'faxReservedAt') : '',
    sending: application.faxSentAt ? formatMenuInstant(application, 'faxSentAt') : '',
    done: application.faxQuotaConsumedAt ? formatMenuInstant(application, 'faxQuotaConsumedAt') : '',
  }
  const transmissionPanelClass = faxProgress.tone === 'danger'
    ? 'border-rose-200 bg-rose-50'
    : faxProgress.tone === 'success'
      ? 'border-emerald-200 bg-emerald-50'
      : faxProgress.tone === 'muted'
        ? 'border-slate-200 bg-slate-50'
        : 'border-sky-100 bg-sky-50'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">청구 신청서 상세</h1>
        <div className="flex items-center gap-2">
          {application.status === 'DRAFT' && (
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
              onClick={() => navigate(`/support/claims?resumeApplicationId=${application.id}`)}
            >
              이어서 작성
            </button>
          )}
          {showFaxRetryAction && (
            <button
              type="button"
              className="rounded border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-800 hover:bg-rose-100 disabled:opacity-50"
              onClick={() => setFaxModalOpen(true)}
              disabled={faxSendDisabled}
              title={faxSendTitle}
            >
              청구 재시도
            </button>
          )}
          <button
            type="button"
            className="rounded border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
            disabled={reclaiming}
            title="내용 복사 후 청구일·첨부·서명만 새로 진행"
            onClick={handleReclaim}
          >
            {reclaiming ? '재청구…' : '재청구'}
          </button>
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
            onClick={() => navigate('/support/claims/applications')}
          >
            목록으로
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(280px,0.9fr)_minmax(0,1.6fr)]">
        <section className="rounded border bg-white p-4">
          <h2 className="text-base font-semibold text-slate-950">기본정보</h2>
          <div className="mt-3">
            <CompactInfoRow label="고객명" value={application.customerName} strong />
            <CompactInfoRow label="연락처" value={application.customerPhone} />
            <CompactInfoRow label="생년월일" value={application.customerBirthDate} />
            <CompactInfoRow label="보험사" value={application.insurerOrgName} strong />
            <CompactInfoRow label="보험사 팩스" value={application.faxReceiveNum} strong />
            {application.faxReceiptNum && (
              <CompactInfoRow label="접수번호" value={application.faxReceiptNum} />
            )}
            <CompactInfoRow label="작성 상태" value={statusLabels[application.status] || application.status} />
            {application.claimAmount && (
              <CompactInfoRow
                label="청구 금액"
                value={`${new Intl.NumberFormat('ko-KR').format(application.claimAmount)}원`}
                strong
              />
            )}
            {application.accidentDate && (
              <CompactInfoRow label="사고일" value={application.accidentDate} />
            )}
            {application.claimDate && (
              <CompactInfoRow label="청구일" value={application.claimDate} />
            )}
            {accidentReceiptTypeLabel && (
              <CompactInfoRow label="접수 구분" value={accidentReceiptTypeLabel} />
            )}
          </div>
          {application.memo && (
            <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <div className="text-xs font-semibold text-slate-500">메모</div>
              <div className="mt-1 whitespace-pre-wrap break-words">{application.memo}</div>
            </div>
          )}
        </section>

        <section className={`rounded border p-4 ${transmissionPanelClass}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-950">전송현황</h2>
              <div className="mt-1 text-2xl font-bold text-slate-950">{faxProgress.title}</div>
              {faxProgress.description && (
                <div className="mt-1 max-w-2xl text-sm text-slate-700">{faxProgress.description}</div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {!showFaxRetryAction && (
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
                  onClick={() => setFaxModalOpen(true)}
                  disabled={faxSendDisabled}
                  title={faxSendTitle}
                >
                  {sendingFax ? '처리 중...' : '보험사로 팩스 발송'}
                </button>
              )}
              {application.faxReservationStatus === 'SCHEDULED' && (
                <button
                  type="button"
                  className="rounded border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
                  onClick={handleCancelScheduledFax}
                >
                  발송 취소
                </button>
              )}
              {showFaxSyncAction && (
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                  onClick={handleRetryFaxSettlement}
                >
                  결과 동기화
                </button>
              )}
            </div>
          </div>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute left-[12.5%] right-[12.5%] top-[18px] hidden h-0.5 bg-slate-200 md:block" />
              <div
                className="absolute left-[12.5%] top-[18px] hidden h-0.5 bg-brand-navy md:block"
                style={{ width: `${Math.min(Math.max(faxProgress.index, 0), 3) * 25}%` }}
              />
              <div className="relative grid gap-4 md:grid-cols-4">
                {FAX_PROGRESS_STEPS.map((step, idx) => (
                  <FaxProgressStep
                    key={step.key}
                    step={step}
                    done={idx < faxProgress.index || (faxProgress.index === 3 && idx === 3)}
                    active={idx === faxProgress.index}
                    danger={faxProgress.tone === 'danger' && idx === faxProgress.index}
                    muted={faxProgress.tone === 'muted' && idx === faxProgress.index}
                    time={faxStepTimes[step.key]}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <DetailInfoItem label="보험사 팩스번호" value={application.faxReceiveNum} strong />
            <DetailInfoItem label="수신자명" value={application.faxReceiveName || application.insurerOrgName} />
            <DetailInfoItem label="팩스 제목" value={application.faxTitle || '보험금청구서'} />
            {application.faxReceiptNum && (
              <DetailInfoItem label="접수번호" value={application.faxReceiptNum} />
            )}
            {application.faxRequestNum && (
              <DetailInfoItem label="요청번호" value={application.faxRequestNum} />
            )}
            {application.faxQuotaReleasedAt && (
              <DetailInfoItem label="종료 시각" value={formatMenuInstant(application, 'faxQuotaReleasedAt')} />
            )}
          </div>

          {application.rejectionReason && (
            <div className="mt-3 rounded border border-red-200 bg-white px-3 py-2 text-sm text-red-700">
              <span className="font-semibold">반려 사유:</span> {application.rejectionReason}
            </div>
          )}
        </section>
      </div>

      <div className="rounded border bg-white p-4">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-semibold text-lg">첨부 서류</h2>
            <div className="mt-0.5 text-xs font-semibold text-slate-500">
              {claimDocuments.length}개 · {claimDocumentPageCountTotal}장
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded border px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
              onClick={handleDownloadAll}
              disabled={downloadingAll}
            >
              {downloadingAll ? '다운로드 중...' : '청구서 다운로드'}
            </button>
          </div>
        </div>

        {claimDocuments.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
            {claimDocuments.map((doc) => {
              const preview = doc.fileId ? documentPreviewUrls[String(doc.fileId)] : null
              const previewKind = preview?.kind || claimAttachmentPreviewKind(doc)
              const previewUrl = preview?.url || ''
              return (
                <div key={doc.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <div className="relative flex h-36 items-center justify-center bg-slate-50 md:h-40">
                    {previewKind === 'image' && previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={doc.label || doc.fileId}
                        className="h-full w-full object-contain"
                      />
                    ) : previewKind === 'pdf' && previewUrl ? (
                      <iframe
                        src={previewUrl}
                        title={doc.label || doc.fileId || '첨부 PDF'}
                        className="pointer-events-none h-full w-full bg-white"
                      />
                    ) : (
                      <span className="text-xs text-slate-400">미리보기</span>
                    )}
                  </div>
                  <div className="space-y-2 p-3">
                    <div className="truncate text-sm font-medium text-slate-900" title={doc.label || doc.fileId}>
                      {doc.label || doc.fileId}
                    </div>
                    <div className="text-[11px] font-medium text-slate-500">{claimDocumentPageCount(doc)}장</div>
                    <select
                      className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs"
                      value={doc.documentType || ''}
                      disabled={documentReviewSavingId === doc.id}
                      onChange={(e) => updateClaimDocumentType(doc, e.target.value)}
                    >
                      {CLAIM_DOCUMENT_TYPE_OPTIONS.map((o) => (
                        <option key={o.value || '_empty'} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        type="button"
                        className="rounded border px-2 py-1.5 text-xs font-medium hover:bg-slate-50"
                        onClick={() => openDocumentPreview(doc)}
                      >
                        미리보기
                      </button>
                      <button
                        type="button"
                        className="rounded border px-2 py-1.5 text-xs font-medium hover:bg-slate-50"
                        onClick={() => downloadFile(doc.fileId)}
                      >
                        다운로드
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
            등록된 첨부 서류가 없습니다.
          </div>
        )}

      </div>

      {faxModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setFaxModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-4">보험사로 팩스 발송</h2>
            <div className="space-y-4">
              {faxInsurerSetting?.virtualFaxRequired && (
                <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  이 보험사는 <span className="font-semibold">가상팩스번호</span>가 필요합니다. 발급받은 번호를 수신번호로 입력해 주세요.
                  {faxListedForVirtualHint
                    ? (
                      <span className="block mt-1 text-amber-800">
                        안내용 번호(이 청구 사고유형 기준): {faxListedForVirtualHint} (이 번호와 동일하면 발송할 수 없습니다)
                      </span>
                      )
                    : null}
                </div>
              )}
              <div>
                <label className="text-sm text-slate-600 mb-1 block">수신번호 *</label>
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={faxForm.receiveNum}
                  onChange={(e) => setFaxForm({ ...faxForm, receiveNum: e.target.value })}
                  placeholder="예: 02-1234-5678"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block">수신자명 *</label>
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={faxForm.receiveName}
                  onChange={(e) => setFaxForm({ ...faxForm, receiveName: e.target.value })}
                  placeholder="예: 삼성화재"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block">제목</label>
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={faxForm.title}
                  onChange={(e) => setFaxForm({ ...faxForm, title: e.target.value })}
                  placeholder="보험금청구서"
                />
              </div>
              <div className="text-xs text-slate-500">
                청구서 PDF와 첨부 서류가 함께 발송됩니다. 약 1분 후 전송이 시작되며, 시작 전까지 발송 취소할 수 있습니다.
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                  onClick={() => {
                    setFaxModalOpen(false)
                    setFaxForm({ receiveNum: '', receiveName: '', title: '' })
                  }}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="rounded bg-brand-navy text-white px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"
                  onClick={handleSendFax}
                  disabled={
                    sendingFax
                    || !faxForm.receiveNum
                    || !faxForm.receiveName
                    || faxTransmissionConsumed
                  }
                >
                  {sendingFax
                    ? '처리 중...'
                    : (application.faxReservationStatus === 'FAILED' || application.faxReservationStatus === 'CANCELLED'
                      ? '발송 재시도'
                      : '발송하기')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded border bg-white p-4">
        <h2 className="font-semibold text-lg mb-4">이력</h2>
        <div className="space-y-2 text-sm">
          {[
            application.createdAt && { label: '생성일', at: application.createdAt },
            application.submittedAt && { label: '제출일', at: application.submittedAt },
            application.reviewedAt && { label: '심사일', at: application.reviewedAt },
            application.completedAt && { label: '완료일', at: application.completedAt },
          ].filter(Boolean).sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).map((row) => (
            <div key={row.label}>
              <span className="text-slate-600">{row.label}:</span>{' '}
              {formatHistoryInstant(row.at)}
            </div>
          ))}
        </div>
        {Array.isArray(application.faxScheduleHistory) && application.faxScheduleHistory.length > 0 && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="font-semibold text-slate-900">보험사 팩스 발송 이력</div>
            <ul className="mt-2 space-y-2">
              {[...application.faxScheduleHistory]
                .sort((a, b) => new Date(b?.at || 0).getTime() - new Date(a?.at || 0).getTime())
                .map((row, idx) => {
                const kind = row?.kind
                const label = faxHistoryKindLabels[kind] || kind || '이벤트'
                const extra = []
                if ((kind === 'RESEND' || kind === 'SCHEDULE_WAIT') && row.delayMinutes != null) {
                  extra.push(`약 ${row.delayMinutes}분 후 전송`)
                }
                if ((kind === 'RESEND' || kind === 'SCHEDULE_WAIT') && row.receiveNum) {
                  extra.push(`수신 ${row.receiveNum}`)
                }
                if ((kind === 'RESEND' || kind === 'SCHEDULE_WAIT') && row.receiveName) {
                  extra.push(String(row.receiveName))
                }
                if (kind === 'FAX_FAILED' && row.message) {
                  extra.push(String(row.message).length > 120 ? `${String(row.message).slice(0, 120)}…` : String(row.message))
                }
                return (
                  <li key={idx} className="border-l-2 border-sky-200 pl-3 text-sm text-slate-700">
                    <span className="text-slate-500">{formatHistoryInstant(row.at)}</span>
                    <span className="mx-1.5 text-slate-300">·</span>
                    <span className="font-medium text-slate-900">{label}</span>
                    {extra.length > 0 && (
                      <span className="block mt-0.5 text-xs text-slate-600">{extra.join(' · ')}</span>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>

      {previewDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded border bg-white shadow-xl">
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">
                  {previewDocument.label || previewDocument.fileId || '첨부파일 미리보기'}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {previewDocumentIndex + 1} / {claimDocuments.length}
                </div>
              </div>
              <button
                type="button"
                className="shrink-0 rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
                onClick={() => setPreviewDocumentIndex(null)}
              >
                닫기
              </button>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-[auto_1fr_auto] items-center gap-2 bg-slate-950 p-3">
              <button
                type="button"
                className="rounded bg-white/90 px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-white disabled:opacity-40"
                onClick={() => moveDocumentPreview(-1)}
                disabled={claimDocuments.length <= 1}
              >
                이전
              </button>
              <div className="flex min-h-[60vh] items-center justify-center overflow-auto rounded bg-slate-900">
                {previewDocumentKind === 'image' ? (
                  <img
                    src={previewDocumentPreview?.url || ''}
                    alt={previewDocument.label || previewDocument.fileId}
                    className="max-h-[72vh] max-w-full object-contain"
                  />
                ) : previewDocumentKind === 'pdf' ? (
                  <iframe
                    src={previewDocumentPreview?.url || ''}
                    title={previewDocument.label || previewDocument.fileId || '첨부 PDF'}
                    className="h-[72vh] w-full min-w-[320px] rounded bg-white"
                  />
                ) : (
                  <div className="rounded bg-white px-6 py-5 text-center text-sm text-slate-700">
                    이 파일 형식은 브라우저 미리보기가 제한될 수 있습니다.
                    <button
                      type="button"
                      className="mt-3 block w-full rounded border px-3 py-2 text-sm font-medium hover:bg-slate-50"
                      onClick={() => downloadFile(previewDocument.fileId)}
                    >
                      다운로드
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="rounded bg-white/90 px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-white disabled:opacity-40"
                onClick={() => moveDocumentPreview(1)}
                disabled={claimDocuments.length <= 1}
              >
                다음
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
